import NavButton from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IFeaturedArticleCard } from '@models/IFeaturedArticleCard';
import { IProductCard } from '@models/IProductCard';
import { IPromoCard } from '@models/IPromoCard';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import ListingCardTypeEnum from '@models/enums/ListingCardTypeEnum';
import Carousel from '@molecules/Carousel/Carousel';
import FeaturedArticleCard from '@molecules/FeaturedArticleCard/FeaturedArticleCard';
import GridProductCard from '@molecules/GridProductCard/GridProductCard';
import PromoCard from '@molecules/PromoCard/PromoCard';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion, useInView } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly title: string;
  readonly tabs: {
    readonly title: string;
    readonly items: (IPromoCard | IProductCard | IFeaturedArticleCard)[];
  }[];
  readonly anchorId?: string;
}

const FeaturedProductsGrid = ({ title, tabs, anchorId }: Props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isSwiperBeginning, setIsSwiperBeginning] = useState(true);
  const [isSwiperEnd, setIsSwiperEnd] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isComponentInView = useInView(sectionRef);
  const isMobileViewport = useMediaQuery(1440);
  const isMobileScreen = useMediaQuery(1440, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobileViewport;
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const isTouchDevice = useIsTouchDevice();
  const mainCard = useMemo(
    () =>
      tabs[activeTabIndex]?.items[0] ||
      ({} as IProductCard | IPromoCard | IFeaturedArticleCard),
    [tabs, activeTabIndex]
  );
  const tiledCards = useMemo(
    () => (tabs[activeTabIndex]?.items.slice(1) || []) as IProductCard[],
    [tabs, activeTabIndex]
  );
  const navButtonId = `featured-products-grid`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    if (isComponentInView) {
      updateDataLayer({
        event: 'viewItemList',
        ecommerce: {
          item_list_name: title,
          items: tiledCards
            ?.filter((product) => product.trackingData)
            .map((product) =>
              mapToNewTrackingData(product.trackingData!, {
                listingName: title,
              })
            ),
        },
      });
    }
  }, [isComponentInView, tabs, tiledCards, title]);

  return (
    <section
      ref={sectionRef}
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-164-Featured-Product-Grid"
      id={`a-${anchorId ?? 'featured-product-grid'}`}
    >
      <div className="container">
        <div className="mb-7.5 flex justify-between gap-5 max-xl:flex-col xl:mb-10 xl:items-center">
          <p className="h2" data-testid="CT-164-Featured-Product-Grid-Title">
            {title}
          </p>

          <div className="flex overflow-hidden">
            <ShadowContainerHorizontal
              className="flex w-full"
              shadowWidth="w-24"
              hasTabbableNestedItems
            >
              <div className="flex w-full gap-5">
                {tabs.length > 1 &&
                  tabs.map(({ title: tabTitle }, index) => (
                    <button
                      key={tabTitle}
                      className={classNames(
                        'body-two flex items-center justify-center gap-2.5 rounded-[100px] px-4 py-1',
                        activeTabIndex === index
                          ? 'bg-black text-white dark:bg-white dark:text-black'
                          : 'bg-grey-200 hover:bg-grey dark:bg-grey-600 hover:dark:bg-grey-500'
                      )}
                      disabled={activeTabIndex === index}
                      onClick={() => {
                        setActiveTabIndex(index);
                      }}
                      data-testid={`CT-164-Featured-Product-Grid-Tab-${index}`}
                    >
                      {tabTitle}
                    </button>
                  ))}
              </div>
            </ShadowContainerHorizontal>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          key={tabs[activeTabIndex]?.title}
        >
          {isMobileViewport ? (
            <div className="flex flex-col gap-7.5">
              {mainCard.type === ListingCardTypeEnum.Promo && (
                <PromoCard
                  {...(mainCard as IPromoCard)}
                  isLargeCard
                  dataTestId="CT-164-Featured-Product-Grid-Main-Promo-Card"
                />
              )}

              {mainCard.type === ListingCardTypeEnum.Product && (
                <GridProductCard
                  {...(mainCard as IProductCard)}
                  isLargeCard
                  dataTestId="CT-164-Featured-Product-Grid-Main-Product-Card"
                />
              )}
              {mainCard.type === ListingCardTypeEnum.Article && (
                <FeaturedArticleCard
                  {...(mainCard as IFeaturedArticleCard)}
                  dataTestId="CT-164-Featured-Product-Grid-Main-Article-Card"
                />
              )}

              <div className="relative w-full">
                <ShadowContainerHorizontal
                  showStart={!isSwiperBeginning}
                  showEnd={!isSwiperEnd}
                  disableScroll
                >
                  <Carousel
                    id="Featured-Products-Grid"
                    analyticsEnabled
                    spaceBetween={20}
                    slidesPerView={1.3}
                    navId={navButtonId}
                    className="whitespace-normal"
                    breakpoints={{
                      768: {
                        slidesPerView: 1.6,
                      },
                      1024: {
                        slidesPerView: 2.3,
                      },
                    }}
                    onSetIsSwiperBeginning={setIsSwiperBeginning}
                    onSetIsSwiperEnd={setIsSwiperEnd}
                  >
                    {tiledCards.map((tiledCard, index) => (
                      <SwiperSlide
                        className="stretch-card"
                        key={tiledCard.name}
                      >
                        <GridProductCard
                          {...tiledCard}
                          dataTestId={`CT-164-Featured-Product-Grid-Product-Card-${index}`}
                        />
                      </SwiperSlide>
                    ))}
                  </Carousel>
                </ShadowContainerHorizontal>

                <div
                  className={classNames(
                    'mt-6 items-center justify-end',
                    isZoomedDesktop ||
                      (screenBreakpoint === BreakpointEnum.Large &&
                        !isTouchDevice)
                      ? 'flex'
                      : 'hidden'
                  )}
                >
                  <div className="flex gap-x-[25px]">
                    <NavButton
                      direction={HorizontalDirectionEnum.Left}
                      id={navButtonId}
                      disabled={isSwiperBeginning}
                    />

                    <NavButton
                      direction={HorizontalDirectionEnum.Right}
                      id={navButtonId}
                      disabled={isSwiperEnd}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-8 grid-rows-2 gap-4">
              <div className="col-span-4 row-span-2">
                {mainCard.type === ListingCardTypeEnum.Promo && (
                  <PromoCard
                    {...(mainCard as IPromoCard)}
                    isLargeCard
                    dataTestId="CT-164-Featured-Product-Grid-Main-Promo-Card"
                  />
                )}

                {mainCard.type === ListingCardTypeEnum.Product && (
                  <GridProductCard
                    {...(mainCard as IProductCard)}
                    isLargeCard
                    dataTestId="CT-164-Featured-Product-Grid-Main-Product-Card"
                  />
                )}

                {mainCard.type === ListingCardTypeEnum.Article && (
                  <FeaturedArticleCard
                    {...(mainCard as IFeaturedArticleCard)}
                    dataTestId="CT-164-Featured-Product-Grid-Main-Article-Card"
                  />
                )}
              </div>

              {tiledCards.map((tiledCard, index) => (
                <div className="col-span-2" key={tiledCard.name}>
                  <GridProductCard
                    {...tiledCard}
                    dataTestId={`CT-164-Featured-Product-Grid-Product-Card-${index}`}
                  />
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProductsGrid;
